import React, { ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAppSelector } from "redux-store/hooks";

import {
  CustomerContainer,
  HeaderDiv,
} from "pages/customers/styles";
import { CancelButtonSec } from "pages/management/privacy-policy/components/details/styles";
import { SaveButton } from "pages/management/privacy-policy/components/details/styles";
import { WARRANTY } from "constants/route/route-constants";
import { addBrandModelWarranty } from "services/warranty-services";
import { getApiErrorMessage } from "utils/api-error-utils";
import { resolveWarrantyPdfForApi } from "utils/warranty-pdf-utils";

import WarrantyPdfPanel from "../components/warranty-pdf-panel";
import { TabContent, WarrantyContainer, ActionButtonsRow } from "../styles";

interface ModelPdfs {
  standard?: string;
  extended?: string;
}

const DEFAULT_WARRANTY_STATUS = "Active";

function AddDefaultWarranty(): ReactElement {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const brandName = useAppSelector((state) => state.brand.brandType);

  const [defaultPdfs, setDefaultPdfs] = useState<ModelPdfs>({});
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    setDefaultPdfs({});
  }, [brandName]);

  const handleCancel = () => {
    history.push(WARRANTY);
  };

  const handleSaveDefault = async () => {
    const standardWarrantyPdf = resolveWarrantyPdfForApi(defaultPdfs.standard, "");
    const extendedWarrantyPdf = resolveWarrantyPdfForApi(defaultPdfs.extended, "");

    if (!standardWarrantyPdf && !extendedWarrantyPdf) {
      enqueueSnackbar("Please upload at least one warranty PDF", {
        variant: "warning",
      });
      return;
    }

    setSaveLoading(true);
    try {
      await addBrandModelWarranty({
        standardWarrantyPdf,
        extendedWarrantyPdf,
        warrantyStatus: DEFAULT_WARRANTY_STATUS,
        isDefault: true,
        carModel: brandName,
      });
      enqueueSnackbar("Default warranty saved successfully", {
        variant: "success",
      });
      history.push(WARRANTY);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Failed to save warranty"), {
        variant: "error",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <CustomerContainer>
      <WarrantyContainer>
        <HeaderDiv />
        <TabContent>
          <WarrantyPdfPanel
            standardPdfUrl={defaultPdfs.standard}
            extendedPdfUrl={defaultPdfs.extended}
            onStandardUpload={(dataUrl) =>
              setDefaultPdfs((prev) => ({ ...prev, standard: dataUrl }))
            }
            onExtendedUpload={(dataUrl) =>
              setDefaultPdfs((prev) => ({ ...prev, extended: dataUrl }))
            }
            onStandardRemove={() =>
              setDefaultPdfs((prev) => {
                const next = { ...prev };
                delete next.standard;
                return next;
              })
            }
            onExtendedRemove={() =>
              setDefaultPdfs((prev) => {
                const next = { ...prev };
                delete next.extended;
                return next;
              })
            }
          />
          <ActionButtonsRow>
            <CancelButtonSec
              variant="outlined"
              color="primary"
              onClick={handleCancel}
            >
              Cancel
            </CancelButtonSec>
            <SaveButton
              variant="contained"
              color="primary"
              onClick={handleSaveDefault}
              disabled={saveLoading}
            >
              {saveLoading ? "Saving..." : "Save"}
            </SaveButton>
          </ActionButtonsRow>
        </TabContent>
      </WarrantyContainer>
    </CustomerContainer>
  );
}

export default AddDefaultWarranty;
